import { Idiomorph } from 'idiomorph';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Parse a rendered document's markup for morphing, without anything that could
 * execute.
 *
 * Idiomorph clones added subtrees with `document.importNode`, which leaves a
 * `<script>` in them executable; the `innerHTML` it replaces never ran one,
 * because fragment parsing marks scripts already-started. Inline handlers
 * survive either way. A document carries Provider-authored rich text, so strip
 * both to keep that property.
 */
const parseWithoutScripts = (markup: string): DocumentFragment => {
  const template = document.createElement('template');
  template.innerHTML = markup;

  for (const script of template.content.querySelectorAll('script')) {
    script.remove();
  }
  for (const element of template.content.querySelectorAll('*')) {
    for (const { name } of [...element.attributes]) {
      if (name.toLowerCase().startsWith('on')) element.removeAttribute(name);
    }
  }

  return template.content;
};

type UseShadowDocumentArgs = {
  /** The rendered document, markup and stylesheet together. */
  html: string;
  /**
   * CSS to apply to the shadow root, one entry per concern, applied in order.
   * For styles a surface adds around the document — the document's own styles
   * come baked into `html`.
   *
   * The hook owns the shadow root's `adoptedStyleSheets` outright, so nothing
   * else may write to them. A new array each render is fine: the entries are
   * compared by content, not by array identity.
   */
  styleSheets?: string[];
};

/**
 * Render a document's markup into a shadow root, and keep a set of stylesheets
 * applied to it.
 *
 * The markup is morphed rather than assigned, so a re-render patches the
 * existing tree instead of rebuilding it — the document keeps its scroll
 * position, and a control inside it keeps its state.
 *
 * Knows nothing about what the document means. A surface that wants clicks,
 * scrolling or live controls adds its own listeners against the returned shadow
 * root.
 */
const useShadowDocument = (args: UseShadowDocumentArgs) => {
  const { html, styleSheets } = args;

  // The root goes in a ref, not state, because a ref callback runs before the
  // effects of the same commit — so the document morphs and a consumer's
  // listeners attach on the first pass rather than a render later. State only
  // marks that the host arrived, to re-run those effects for a consumer that
  // renders a loader until its document loads.
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const [attachedHost, setAttachedHost] = useState<HTMLDivElement | null>(null);
  const hostRef = useCallback((node: HTMLDivElement | null) => {
    shadowRootRef.current = node
      ? (node.shadowRoot ?? node.attachShadow({ mode: 'open' }))
      : null;
    setAttachedHost(node);
  }, []);

  useEffect(() => {
    const shadowRoot = shadowRootRef.current;
    if (!shadowRoot) return;

    // Idiomorph's own `restoreFocus` reads bare `document.activeElement`, which
    // inside a shadow root is the host rather than the control being used, so
    // it cannot see what to put focus back on. Turn it off and restore against
    // the shadow root instead.
    const focusedId = shadowRoot.activeElement?.id || null;

    Idiomorph.morph(shadowRoot, parseWithoutScripts(html), {
      morphStyle: 'innerHTML',
      restoreFocus: false,
    });

    if (focusedId && shadowRoot.activeElement?.id !== focusedId) {
      shadowRoot
        .querySelector<HTMLElement>(`[id="${CSS.escape(focusedId)}"]`)
        ?.focus({ preventScroll: true });
    }
  }, [attachedHost, html]);

  // One sheet from the joined CSS, so the effect keys on the styles themselves.
  // Keying on the array would re-adopt on every render of a consumer that
  // builds it inline. Order within the sheet is the array's order, so the
  // cascade is unchanged.
  const css = (styleSheets ?? []).join('\n');
  useEffect(() => {
    const shadowRoot = shadowRootRef.current;
    if (!shadowRoot) return;

    // Adopted stylesheets live on the shadow root itself, so an innerHTML morph
    // leaves them intact — which is why they are used here over a `<style>` tag.
    if (!css) {
      shadowRoot.adoptedStyleSheets = [];
      return;
    }

    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    shadowRoot.adoptedStyleSheets = [sheet];

    return () => {
      shadowRoot.adoptedStyleSheets = [];
    };
  }, [attachedHost, css]);

  return {
    hostRef,
    /**
     * Read inside an effect, never during render — it is populated when the
     * host commits. Key such an effect on `hostAttached` so it re-runs once the
     * host arrives.
     */
    shadowRootRef,
    hostAttached: attachedHost !== null,
  };
};

export default useShadowDocument;
